import { MaterialCommunityIcons } from "@expo/vector-icons";
import { CHIME_CONFIRMATION_LABEL, checkBankAnswer } from "@taskhealth/shared";
import { LinearGradient } from "expo-linear-gradient";
import { useRouter, useLocalSearchParams, Stack } from "expo-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
    StyleSheet,
    View,
    ScrollView,
    TouchableOpacity,
    ActivityIndicator,
    KeyboardAvoidingView,
    Platform
} from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { AiVerificationAnimation } from "@/components/onboarding/AiVerificationAnimation";
import { OnboardingItemRenderer } from "@/components/onboarding/OnboardingItemRenderer";
import { PhotoUploadSection } from "@/components/onboarding/PhotoUploadSection";
import { ShareReferenceButton } from "@/components/onboarding/ShareReferenceButton";
import { UploadHistory } from "@/components/onboarding/UploadHistory";
import { UploadStagingModal } from "@/components/onboarding/UploadStagingModal";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { useOnboardingRefresh } from "@/hooks/useOnboardingWebSocket";
import { useUploadFlow } from "@/hooks/useUploadFlow";
import { useAuth } from "@/lib/auth";
import { MOBILE_DEBUG_VERSION } from "@/lib/debug";
import {
    fetchOnboardingState,
    saveOnboardingAnswers,
    type ObSubsectionNode,
    type ClassificationRejection
} from "@/lib/onboarding-api";
import { evaluateOnboardingVisibility } from "@/lib/onboarding-branching";
import { partitionPhotoSectionItems } from "@/lib/onboarding-items";
import { colors, fonts, gradients, radius, shadows, spacing, typography } from "@/lib/theme";

/**
 * Item types whose answer can only change by a discrete tap — these commit to
 * the server immediately. Everything typed (text, email, ssn, phone, numeric)
 * rides the 3s debounce; saving per keystroke would be a request per character.
 *
 * Mirrors TAP_ANSWER_TYPES in lib/FormContext.tsx. The two lists are separate
 * on purpose: onboarding items and visit questions use different type
 * vocabularies ("checkbox"/"yes_no" here, "check"/"yesNo" there).
 *
 * `signature` / `compliance_form` are excluded for the same reason as in the
 * visit form — SignatureInput's "unable to sign" branch writes a free-text
 * reason per keystroke.
 */
const TAP_ITEM_TYPES = new Set(["radio", "checkbox", "dropdown", "yes_no", "date"]);

export default function SubsectionScreen() {
    const router = useRouter();
    const { user } = useAuth();
    const { subsectionId, rnId, sectionId } = useLocalSearchParams<{
        subsectionId: string;
        rnId?: string;
        sectionId?: string;
    }>();

    const [subsection, setSubsection] = useState<ObSubsectionNode | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [answers, setAnswers] = useState<Record<string, string>>({});
    //  Per-item "I confirm this is not a Chime account", for partner-bank
    //  routing numbers we can only suspect. Deliberately NOT persisted: it
    //  gates this screen's Save, and asking again on a later visit costs one
    //  tick, where a remembered tick could carry past a changed answer.
    const [bankConfirmations, setBankConfirmations] = useState<Record<string, boolean>>({});
    const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
    const [classRejections, setClassRejections] = useState<ClassificationRejection[]>([]);
    const { dialogState, showDialog, dismissDialog } = useConfirmDialog();
    const resolvedRnId = rnId ?? "";
    const onboardingHref = resolvedRnId ? `/onboarding?rnId=${resolvedRnId}` : "/onboarding";
    const sectionHref = sectionId
        ? `/onboarding/section/${sectionId}?rnId=${resolvedRnId}`
        : onboardingHref;

    // Track dirty answers for debounced save
    const dirtyRef = useRef<Map<string, string>>(new Map());
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Tail of the serialized save chain — see flushSaves.
    const inflightRef = useRef<Promise<void> | null>(null);

    useEffect(() => {
        if (!subsectionId || !resolvedRnId) {
            return;
        }
        setLoading(true);
        fetchOnboardingState(resolvedRnId)
            .then((state) => {
                if (state.classificationRejections) {
                    // Only what was rejected on THIS screen. Unscoped, these
                    // followed the nurse around the app: an identity-step
                    // "this is a driving license" rendered on the vaccines page.
                    setClassRejections(
                        state.classificationRejections.filter(
                            (r) => String(r.subsectionId) === subsectionId
                        )
                    );
                }
                for (const section of state.config) {
                    const sub = section.subsections.find((s) => String(s.id) === subsectionId);
                    if (sub) {
                        setSubsection(sub);
                        // Initialize answers from submissions
                        const map: Record<string, string> = {};
                        for (const item of sub.items) {
                            if (item.submission?.answerValue) {
                                map[item.id] = item.submission.answerValue;
                            }
                        }
                        setAnswers(map);
                        break;
                    }
                }
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, [subsectionId, resolvedRnId]);

    // Auto-save with debounce
    function scheduleFlush() {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        // flushSaves() rethrows on failure so handleSave()'s try/catch can react
        // to it, but this debounced timer has nothing awaiting it — the rethrow
        // became an unhandled promise rejection on every failed autosave
        // (e.g. a validation 400 from the backend). The UI feedback (dirty-set
        // restore, saveStatus("error")) already happened inside flushSaves.
        timerRef.current = setTimeout(() => {
            flushSaves().catch(() => {});
        }, 3000);
    }

    /**
     * Serialized flush, same shape as the visit form's AutoSaveEngine (see
     * lib/autosave.ts). Tap answers commit on touch, so two flushes overlapping
     * is routine rather than rare — and each one drains `dirtyRef` into its own
     * request, so without chaining they would both read the same pending set.
     * Each call runs after the previous one has settled.
     */
    function flushSaves(): Promise<void> {
        const prev = inflightRef.current ?? Promise.resolve();
        const pass = prev.catch(() => {}).then(() => runSavePass());
        inflightRef.current = pass;
        return pass.finally(() => {
            // Only the tail clears the chain; an earlier link must not.
            if (inflightRef.current === pass) {
                inflightRef.current = null;
            }
        });
    }

    async function runSavePass() {
        if (dirtyRef.current.size === 0) {
            return;
        }
        const pending = Array.from(dirtyRef.current.entries()).map(([itemId, value]) => ({
            itemId,
            answerValue: value
        }));
        dirtyRef.current.clear();

        setSaveStatus("saving");
        try {
            await saveOnboardingAnswers(resolvedRnId, pending);
            setSaveStatus("saved");
        } catch (err) {
            // Re-add on failure — but never over a newer value. The nurse can
            // now change an answer while a save is in flight, and restoring the
            // stale one would undo what she just tapped.
            for (const p of pending) {
                if (!dirtyRef.current.has(p.itemId)) {
                    dirtyRef.current.set(p.itemId, p.answerValue);
                }
            }
            setSaveStatus("error");
            throw err;
        }
    }

    function handleAnswerChange(itemId: string, value: string) {
        setAnswers((prev) => ({ ...prev, [itemId]: value }));
        setSaveStatus("idle");

        const item = subsection?.items.find((i) => String(i.id) === String(itemId));

        const bankCheck = item ? checkBankAnswer(item.name, value) : null;

        //  Changing a bank answer withdraws any confirmation she gave for the
        //  old one — otherwise ticking "not Chime" for one routing number would
        //  silently clear the warning for the next one she types.
        if (bankCheck?.level === "confirm" || bankConfirmations[itemId]) {
            setBankConfirmations((prev) => (prev[itemId] ? { ...prev, [itemId]: false } : prev));
        }

        //  A bank name that says Chime is never sent. The API refuses it anyway
        //  (see unsupported-bank.ts), but letting the debounced autosave fire
        //  means a red "Couldn't save" chip in the header arguing with the
        //  field's own alert, which is the explanation that actually tells her
        //  what is wrong. Keep her keystrokes on screen — she has to see what
        //  she typed to change it — and drop any earlier value of this item
        //  still queued, so nothing half-typed gets saved behind the alert.
        //
        //  A "confirm" answer IS saved: it is very probably a normal account,
        //  and holding it hostage to a tick-box would lose her typing if she
        //  navigated away before reading the warning.
        if (bankCheck?.level === "block") {
            dirtyRef.current.delete(itemId);
            return;
        }

        dirtyRef.current.set(itemId, value);

        const itemType = item?.itemType;
        if (itemType && TAP_ITEM_TYPES.has(itemType)) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            // Fire-and-forget: runSavePass re-queues on failure and surfaces
            // the error banner; blocking a radio tap on the network would make
            // the form feel broken offline.
            void flushSaves().catch(() => {});
        } else {
            scheduleFlush();
        }
    }

    async function handleSave() {
        //  Refuse to finish the section while a bank answer is unresolved —
        //  either Chime outright, or a partner-bank account she has not yet
        //  confirmed. The danger is not bad data (a blocked value was never
        //  queued) but her walking away believing Direct Deposit is done.
        //  Back still works: she can leave and return.
        if (pendingBank) {
            showDialog(
                pendingBank.check.level === "block"
                    ? "We can't accept this account"
                    : "Please confirm this is not Chime",
                pendingBank.check.level === "block"
                    ? pendingBank.check.message
                    : `${pendingBank.check.message}\n\nTick "${CHIME_CONFIRMATION_LABEL}" under ${pendingBank.item.name} to carry on.`
            );
            return;
        }
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        setSaving(true);
        try {
            await flushSaves();
            handleBack();
        } catch (err) {
            showDialog("Save failed", err instanceof Error ? err.message : "Please try again.");
        } finally {
            setSaving(false);
        }
    }

    function handleBack() {
        console.info(`[mobile ${MOBILE_DEBUG_VERSION}] subsection back pressed`, {
            subsectionId,
            sectionHref
        });
        // Flush any pending debounced answers (e.g. a signature just drawn)
        // BEFORE navigating away, otherwise the 3s auto-save timer is abandoned
        // on unmount and the answer is lost. Fire-and-forget: navigation should
        // not wait on the network, but the save must be kicked off first.
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        // `void` discards the return value, not the rejection — flushSaves()
        // rethrows on failure, so this needs its own catch (see scheduleFlush).
        flushSaves().catch(() => {});
        router.navigate(sectionHref);
    }

    // Compute visible items based on branching
    const visibleItems = useMemo(() => {
        if (!subsection) {
            return new Set<string>();
        }
        return evaluateOnboardingVisibility(subsection.items, answers);
    }, [subsection, answers]);

    //  The Chime rule at section level: the first visible bank field still
    //  standing in the way — named Chime, or a partner bank she has not yet
    //  confirmed. Only gates Save; the field's own notice, rendered by
    //  OnboardingItemRenderer, is what explains it where she is looking.
    const pendingBank = useMemo(() => {
        if (!subsection) {
            return null;
        }
        for (const item of subsection.items) {
            if (!visibleItems.has(item.id)) {
                continue;
            }
            const check = checkBankAnswer(item.name, answers[item.id]);
            if (!check) {
                continue;
            }
            if (check.level === "block" || !bankConfirmations[item.id]) {
                return { item, check };
            }
        }
        return null;
    }, [subsection, visibleItems, answers, bankConfirmations]);

    // Test docs picker state — Files button opens this
    const [showTestDocsPicker, setShowTestDocsPicker] = useState(false);

    // Upload flow for photo_upload_section bottom bar
    //  A just-finished upload has not necessarily been recorded yet: the AI
    //  job is enqueued asynchronously, so the immediate refetch below usually
    //  still sees the OLD status. That mattered because `hasPendingAi` is
    //  derived from what the refetch saw, and the 30s fallback poll is gated on
    //  it — so a refetch that arrived a moment too early meant nothing pending
    //  was ever observed, the poll never started, and the screen sat unchanged
    //  until the nurse navigated away and back. This window keeps us looking
    //  for a short while after an upload, regardless of what that first refetch
    //  happened to catch.
    const [settlingUntil, setSettlingUntil] = useState(0);
    //  Set by the "Need to change something?" escape below. Declared up here
    //  with the other hooks because the render has early returns.
    const [unlockedForEdit, setUnlockedForEdit] = useState(false);

    const uploadFlow = useUploadFlow({
        rnId: resolvedRnId,
        onComplete: () => {
            setSettlingUntil(Date.now() + 20_000);
            refetchState();
        }
    });

    // Detect pending AI items — used for animation and fallback poll
    const UPLOAD_TYPES = new Set(["document_upload", "camera"]);
    const PENDING_STATUSES = new Set(["submitted", "ai_verifying"]);
    const hasPendingAi = !!subsection?.items.some(
        (i) => UPLOAD_TYPES.has(i.itemType) && PENDING_STATUSES.has(i.submission?.status ?? "")
    );

    // Refetch function shared by WebSocket and fallback poll
    function refetchState() {
        if (!resolvedRnId || !subsectionId) {
            return;
        }
        fetchOnboardingState(resolvedRnId)
            .then((state) => {
                if (state.classificationRejections) {
                    // Only what was rejected on THIS screen. Unscoped, these
                    // followed the nurse around the app: an identity-step
                    // "this is a driving license" rendered on the vaccines page.
                    setClassRejections(
                        state.classificationRejections.filter(
                            (r) => String(r.subsectionId) === subsectionId
                        )
                    );
                }
                for (const section of state.config) {
                    const sub = section.subsections.find((s) => String(s.id) === subsectionId);
                    if (sub) {
                        setSubsection(sub);
                        const map: Record<string, string> = {};
                        for (const item of sub.items) {
                            if (item.submission?.answerValue) {
                                map[item.id] = item.submission.answerValue;
                            }
                        }
                        setAnswers(map);
                        break;
                    }
                }
            })
            .catch(() => {}); // silently ignore refetch errors
    }

    // Primary: WebSocket push — instant updates from backend workers
    useOnboardingRefresh(resolvedRnId, refetchState);

    // Fallback: slow poll (30s) in case WebSocket disconnects while items are pending
    useEffect(() => {
        if (!hasPendingAi || !resolvedRnId || !subsectionId) {
            return;
        }
        const interval = setInterval(refetchState, 30_000);
        return () => clearInterval(interval);
    }, [hasPendingAi, resolvedRnId, subsectionId]);

    //  Settling window: poll briskly for a short spell after an upload, so the
    //  status the nurse is waiting on appears on this screen rather than only
    //  after she leaves and comes back. Bounded by `settlingUntil` so a bad
    //  upload can never leave the app polling forever; once the item shows as
    //  pending, the 30s poll above owns it.
    useEffect(() => {
        if (!resolvedRnId || !subsectionId || settlingUntil === 0) {
            return;
        }
        const interval = setInterval(() => {
            if (Date.now() > settlingUntil) {
                setSettlingUntil(0);
                return;
            }
            refetchState();
        }, 3_000);
        return () => clearInterval(interval);
    }, [settlingUntil, resolvedRnId, subsectionId]);

    // Cleanup
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    if (loading) {
        return (
            <View style={styles.center}>
                <ActivityIndicator size="large" color={colors.teal400} />
            </View>
        );
    }

    if (error || !subsection) {
        return (
            <View style={styles.center}>
                <MaterialCommunityIcons
                    name="alert-circle-outline"
                    size={48}
                    color={colors.error500}
                />
                <Text style={styles.errorText}>{error ?? "Subsection not found"}</Text>
            </View>
        );
    }

    const isPhotoUploadSection = subsection.subsectionType === "photo_upload_section";

    // Check if all visible form items are already approved (previously submitted)
    const allApproved =
        !isPhotoUploadSection &&
        subsection.items
            .filter((item) => visibleItems.has(item.id) && item.itemType !== "plain_text")
            .every((item) => item.submission?.status === "approved");

    //  A fully-approved subsection used to be read-only with no way out, and
    //  text answers approve the instant they are saved. So a typo was permanent:
    //  Ashley Allen entered her own name as her reference's name on 1 September
    //  2026 and the section sealed behind her, with the reference request
    //  already sent to a stranger.
    //
    //  The escape is offered only where the answers are HERS to restate —
    //  contact details, who her reference is. Anything backed by evidence
    //  (an upload, a signature, a completed course) stays locked, because
    //  re-opening those would let approved proof be swapped after the fact.
    const EVIDENCE_TYPES = ["document_upload", "signature", "camera", "course_bundle"];
    const isRestatable =
        allApproved &&
        subsection.items
            .filter((item) => visibleItems.has(item.id))
            .every((item) => !EVIDENCE_TYPES.includes(item.itemType));

    const isReadOnly = allApproved && !(isRestatable && unlockedForEdit);

    const visibleItemsSorted = subsection.items
        .filter((item) => visibleItems.has(item.id))
        .sort((a, b) => a.orderIndex - b.orderIndex);

    // For photo_upload_section: questions render as fields, documents as upload
    // tiles. Questions come first because that is the order they are asked in:
    // say what kind of test you have, then upload it. See the helper for why
    // rendering the non-upload items at all is load-bearing.
    const { questions: questionItems, uploads: uploadItems } =
        partitionPhotoSectionItems(visibleItemsSorted);

    // Reference sharing: show "Share with your Reference" button for Reference 1/2 subsections
    const isReferenceSubsection =
        subsection.name.includes("Reference") && !subsection.name.includes("Affidavit");
    const firstItem = visibleItemsSorted[0];
    const isReferenceOptionSelected =
        isReferenceSubsection && firstItem && answers[firstItem.id] === "Reference";
    const fullNameItem = visibleItemsSorted.find((item) =>
        item.name.toLowerCase().includes("full name")
    );
    const referenceName = fullNameItem ? (answers[fullNameItem.id] ?? "") : "";

    // Proof-of-Work: gate the employment-verification upload on the workplace
    // name being filled first, so AI verification never starts on a document
    // with no workplace to check it against.
    const workplaceItem = visibleItemsSorted.find((item) =>
        item.name.toLowerCase().includes("workplace")
    );
    const workplaceFilled = workplaceItem ? !!answers[workplaceItem.id]?.trim() : true;

    // Progress counts
    const totalVisible = visibleItemsSorted.filter((i) => i.itemType !== "plain_text").length;
    const completedVisible = visibleItemsSorted.filter(
        (i) =>
            i.itemType !== "plain_text" &&
            (i.submission?.status === "approved" || i.submission?.status === "not_required")
    ).length;

    return (
        <>
            <Stack.Screen options={{ headerShown: false }} />
            <SafeAreaView style={styles.safeArea} edges={["top", "bottom"]}>
                <View style={styles.container}>
                    <View style={styles.topBar}>
                        <TouchableOpacity
                            onPress={handleBack}
                            style={styles.backButton}
                            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                        >
                            <MaterialCommunityIcons
                                name="chevron-left"
                                size={28}
                                color={colors.textPrimary}
                            />
                        </TouchableOpacity>
                        <Text style={styles.topBarTitle} numberOfLines={1} ellipsizeMode="tail">
                            {subsection.name}
                        </Text>
                        <View style={styles.topBarSpacer} />
                    </View>

                    {/* Sub-header with progress + auto-save status */}
                    <View style={styles.subHeader}>
                        <Text style={styles.subHeaderTitle}>{subsection.name}</Text>
                        <View style={styles.subHeaderRight}>
                            {saveStatus === "saving" && (
                                <View style={styles.saveStatusRow}>
                                    <ActivityIndicator size="small" color={colors.textTertiary} />
                                    <Text style={styles.saveStatusText}>Saving…</Text>
                                </View>
                            )}
                            {saveStatus === "saved" && (
                                <View style={styles.saveStatusRow}>
                                    <MaterialCommunityIcons
                                        name="check-circle"
                                        size={14}
                                        color={colors.success500}
                                    />
                                    <Text
                                        style={[
                                            styles.saveStatusText,
                                            { color: colors.success500 }
                                        ]}
                                    >
                                        Saved
                                    </Text>
                                </View>
                            )}
                            {saveStatus === "error" && (
                                <View style={styles.saveStatusRow}>
                                    <MaterialCommunityIcons
                                        name="alert-circle"
                                        size={14}
                                        color={colors.error500}
                                    />
                                    <Text
                                        style={[styles.saveStatusText, { color: colors.error500 }]}
                                    >
                                        Couldn&apos;t save
                                    </Text>
                                </View>
                            )}
                            <Text style={styles.subHeaderProgress}>
                                {completedVisible}/{totalVisible}
                            </Text>
                        </View>
                    </View>

                    <KeyboardAvoidingView
                        style={styles.flexFill}
                        behavior={Platform.OS === "ios" ? "padding" : "height"}
                    >
                        <ScrollView
                            style={styles.scroll}
                            contentContainerStyle={styles.scrollContent}
                            keyboardShouldPersistTaps="handled"
                            automaticallyAdjustKeyboardInsets={Platform.OS === "ios"}
                        >
                            {isPhotoUploadSection ? (
                                /* photo_upload_section: questions, then checklist + upload buttons */
                                <View style={styles.photoSectionWrap}>
                                    {questionItems.map((item) => (
                                        <OnboardingItemRenderer
                                            key={item.id}
                                            item={item}
                                            value={answers[item.id]}
                                            onChange={(v) => handleAnswerChange(item.id, v)}
                                            rnId={resolvedRnId}
                                            onUploadComplete={refetchState}
                                        />
                                    ))}

                                    <PhotoUploadSection
                                        rnId={resolvedRnId}
                                        items={uploadItems}
                                        onUploadComplete={refetchState}
                                    />

                                    {/* Classification-level rejections */}
                                    {classRejections.length > 0 && (
                                        <View style={styles.classRejectionContainer}>
                                            {classRejections.map((r) => (
                                                <View
                                                    key={r.id}
                                                    style={styles.classRejectionBanner}
                                                >
                                                    <MaterialCommunityIcons
                                                        name="close-circle"
                                                        size={18}
                                                        color={colors.error500}
                                                    />
                                                    <Text style={styles.classRejectionText}>
                                                        {r.rejectionReason}
                                                    </Text>
                                                </View>
                                            ))}
                                        </View>
                                    )}

                                    {/* Upload history for THIS section only */}
                                    <UploadHistory
                                        rnId={resolvedRnId}
                                        subsectionId={subsection.id}
                                        reloadToken={uploadItems
                                            .map((i) => `${i.id}:${i.submission?.status ?? ""}`)
                                            .join(",")}
                                    />
                                </View>
                            ) : (
                                /* Regular form: render items individually */
                                <>
                                    {visibleItemsSorted.map((item) => {
                                        const hasAiVerification =
                                            item.aiConfig?.verificationMethod &&
                                            item.aiConfig.verificationMethod !== "none";
                                        const blockedReason =
                                            item.itemType === "document_upload" &&
                                            item.name
                                                .toLowerCase()
                                                .includes("employment verification") &&
                                            workplaceItem &&
                                            !workplaceFilled
                                                ? "Enter the workplace name above before uploading — verification starts once it's filled in."
                                                : undefined;
                                        return (
                                            <OnboardingItemRenderer
                                                key={item.id}
                                                item={item}
                                                value={answers[item.id]}
                                                onChange={(v) => handleAnswerChange(item.id, v)}
                                                readOnly={isReadOnly}
                                                hideStatus={isReadOnly && !hasAiVerification}
                                                rnId={resolvedRnId}
                                                onUploadComplete={refetchState}
                                                blockedReason={blockedReason}
                                                bankConfirmed={bankConfirmations[item.id]}
                                                onBankConfirm={(confirmed) =>
                                                    setBankConfirmations((prev) => ({
                                                        ...prev,
                                                        [item.id]: confirmed
                                                    }))
                                                }
                                            />
                                        );
                                    })}
                                    <ShareReferenceButton
                                        referenceName={referenceName}
                                        rnFullName={user?.name ?? "RN"}
                                        visible={!!isReferenceOptionSelected}
                                        rnId={Number(resolvedRnId) || null}
                                        subsectionId={Number(subsectionId) || null}
                                    />
                                    {isRestatable && !unlockedForEdit && (
                                        <TouchableOpacity
                                            style={styles.unlockEdit}
                                            onPress={() => setUnlockedForEdit(true)}
                                            accessibilityRole="button"
                                        >
                                            <Text style={styles.unlockEditText}>
                                                Need to change something? Tap to edit these answers.
                                            </Text>
                                        </TouchableOpacity>
                                    )}
                                </>
                            )}

                            {/* AI verification animation -- shown when documents are being processed */}
                            {hasPendingAi && <AiVerificationAnimation />}
                        </ScrollView>
                    </KeyboardAvoidingView>

                    {/* Footer button -- only for form type */}
                    {!isPhotoUploadSection && (
                        <View style={styles.footer}>
                            <TouchableOpacity
                                onPress={handleSave}
                                disabled={saving}
                                activeOpacity={0.8}
                            >
                                <LinearGradient
                                    colors={[...gradients.brand]}
                                    start={{ x: 0, y: 0 }}
                                    end={{ x: 1, y: 0 }}
                                    style={[
                                        styles.footerButtonGradient,
                                        (saving || pendingBank != null) && { opacity: 0.7 }
                                    ]}
                                >
                                    {saving ? (
                                        <ActivityIndicator size="small" color={colors.surface} />
                                    ) : (
                                        <Text style={styles.footerButtonText}>Save</Text>
                                    )}
                                </LinearGradient>
                            </TouchableOpacity>
                        </View>
                    )}

                    {/* Bottom camera bar for photo upload sections */}
                    {isPhotoUploadSection && (
                        <View style={styles.cameraBar}>
                            {/* Photos / Gallery button */}
                            <View style={styles.cameraBarSide}>
                                <TouchableOpacity
                                    style={styles.smallCircleBtn}
                                    onPress={uploadFlow.openGallery}
                                    disabled={uploadFlow.uploading}
                                >
                                    <MaterialCommunityIcons
                                        name="image-outline"
                                        size={20}
                                        color={colors.teal600}
                                    />
                                </TouchableOpacity>
                                <Text style={styles.cameraBarLabel}>Gallery</Text>
                            </View>

                            {/* Center: large camera button */}
                            <View style={styles.cameraBarCenter}>
                                <TouchableOpacity
                                    activeOpacity={0.8}
                                    onPress={uploadFlow.openCamera}
                                    disabled={uploadFlow.uploading}
                                >
                                    <LinearGradient
                                        colors={[...gradients.teal]}
                                        start={{ x: 0, y: 0 }}
                                        end={{ x: 1, y: 1 }}
                                        style={[styles.largeCameraBtn, shadows.brand]}
                                    >
                                        <MaterialCommunityIcons
                                            name="camera"
                                            size={32}
                                            color={colors.surface}
                                        />
                                    </LinearGradient>
                                </TouchableOpacity>
                            </View>

                            {/* Files button — opens test docs picker */}
                            <View style={styles.cameraBarSide}>
                                <TouchableOpacity
                                    style={styles.smallCircleBtn}
                                    onPress={() => setShowTestDocsPicker(true)}
                                    disabled={uploadFlow.uploading}
                                >
                                    <MaterialCommunityIcons
                                        name="file-document-outline"
                                        size={20}
                                        color={colors.teal600}
                                    />
                                </TouchableOpacity>
                                <Text style={styles.cameraBarLabel}>Files</Text>
                            </View>
                        </View>
                    )}

                    {/* Staging modal for multi-image upload */}
                    <UploadStagingModal
                        visible={uploadFlow.showStaging}
                        stagedImages={uploadFlow.stagedImages}
                        onAddMoreCamera={uploadFlow.openCamera}
                        onAddMoreGallery={uploadFlow.openGallery}
                        onRemove={uploadFlow.removeStaged}
                        onUpload={uploadFlow.uploadAll}
                        onCancel={uploadFlow.cancelStaging}
                        uploading={uploadFlow.uploading}
                        uploadProgress={uploadFlow.uploadProgress}
                    />
                    <ConfirmDialog {...dialogState} onDismiss={dismissDialog} />
                </View>
            </SafeAreaView>
        </>
    );
}

const styles = StyleSheet.create({
    unlockEdit: {
        marginTop: 16,
        paddingVertical: 10,
        alignItems: "center"
    },
    unlockEditText: {
        fontSize: 13,
        fontWeight: "600",
        color: colors.teal600
    },
    safeArea: {
        flex: 1,
        backgroundColor: colors.background
    },
    container: {
        flex: 1,
        backgroundColor: colors.background
    },
    flexFill: {
        flex: 1
    },
    scroll: {
        flex: 1
    },
    scrollContent: {
        paddingHorizontal: spacing.base,
        paddingBottom: 120
    },
    center: {
        flex: 1,
        justifyContent: "center",
        alignItems: "center",
        gap: spacing.md,
        padding: spacing.xl
    },
    errorText: {
        ...typography.body,
        color: colors.error500,
        textAlign: "center"
    },

    topBar: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: spacing.base,
        paddingTop: spacing.xs,
        paddingBottom: spacing.sm
    },
    topBarTitle: {
        flex: 1,
        textAlign: "center",
        fontSize: 18,
        fontFamily: fonts.bold,
        color: colors.textPrimary
    },
    topBarSpacer: {
        width: 34
    },

    // ── Sub-header ──
    subHeader: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        paddingHorizontal: spacing.base,
        paddingVertical: spacing.md,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    subHeaderTitle: {
        ...typography.h2,
        color: colors.textPrimary,
        flex: 1
    },
    subHeaderProgress: {
        ...typography.subtitle,
        color: colors.teal600
    },
    subHeaderRight: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md
    },
    saveStatusRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs
    },
    saveStatusText: {
        ...typography.caption,
        color: colors.textTertiary
    },

    // ── Footer ──
    footer: {
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        padding: spacing.base,
        backgroundColor: colors.surface,
        borderTopWidth: 1,
        borderTopColor: colors.border
    },
    footerButtonGradient: {
        borderRadius: radius.full,
        paddingVertical: 14,
        alignItems: "center",
        justifyContent: "center",
        ...shadows.brand
    },
    footerButtonText: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.surface
    },

    // ── Back button ──
    backButton: {
        width: 44,
        height: 44,
        borderRadius: 12,
        alignItems: "center",
        justifyContent: "center",
        marginLeft: -10
    },

    // ── Classification rejection banners ──
    classRejectionContainer: {
        gap: spacing.sm,
        marginTop: spacing.sm
    },
    classRejectionBanner: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.sm,
        backgroundColor: colors.error50,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: colors.error500,
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.md
    },
    classRejectionText: {
        ...typography.caption,
        color: colors.error500,
        flex: 1
    },

    // ── Success banner ──
    successBanner: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.sm,
        backgroundColor: colors.success50,
        borderRadius: radius.lg,
        borderWidth: 1.5,
        borderColor: colors.success400,
        paddingVertical: 14,
        marginTop: spacing.sm,
        marginBottom: spacing.xs
    },
    successText: {
        ...typography.subtitle,
        color: colors.success500
    },

    // ── Photo upload section ──
    photoSectionWrap: {
        gap: spacing.md,
        paddingTop: spacing.sm
    },

    // ── Document item cards ──
    docItemCard: {
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        borderWidth: 1.5,
        borderColor: colors.border,
        padding: spacing.base,
        ...shadows.xs
    },
    docItemRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md
    },
    docIconCircle: {
        width: 40,
        height: 40,
        borderRadius: radius.sm,
        backgroundColor: colors.teal50,
        alignItems: "center",
        justifyContent: "center"
    },
    docIconCircleApproved: {
        backgroundColor: colors.success50
    },
    docIconCircleRejected: {
        backgroundColor: colors.error50
    },
    docItemInfo: {
        flex: 1
    },
    docItemName: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    docItemDesc: {
        ...typography.caption,
        color: colors.textSecondary,
        marginTop: 2
    },
    docItemRejection: {
        ...typography.caption,
        color: colors.error500,
        marginTop: spacing.xs
    },
    docItemRight: {
        alignItems: "center",
        justifyContent: "center"
    },
    completionCircleSm: {
        width: 22,
        height: 22,
        borderRadius: 11,
        backgroundColor: colors.success500,
        alignItems: "center",
        justifyContent: "center"
    },
    emptyCircle: {
        width: 22,
        height: 22,
        borderRadius: 11,
        borderWidth: 2,
        borderColor: colors.border
    },

    // ── Bottom camera bar ──
    cameraBar: {
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "center",
        paddingHorizontal: spacing.xl,
        paddingTop: spacing.md,
        paddingBottom: spacing.lg,
        backgroundColor: colors.surface,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        gap: spacing["2xl"]
    },
    cameraBarSide: {
        alignItems: "center",
        gap: spacing.xs
    },
    smallCircleBtn: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: colors.teal50,
        alignItems: "center",
        justifyContent: "center",
        borderWidth: 1.5,
        borderColor: colors.border
    },
    cameraBarLabel: {
        ...typography.micro,
        color: colors.textTertiary,
        textTransform: "uppercase"
    },
    cameraBarCenter: {
        alignItems: "center",
        marginBottom: spacing.sm
    },
    largeCameraBtn: {
        width: 72,
        height: 72,
        borderRadius: 36,
        alignItems: "center",
        justifyContent: "center"
    }
});
